import { Subscription } from '@rails/actioncable';
import { BulkOperation_Read, BulkOperationStatus } from '@treadinc/horizon-api-spec';
import { omit, uniq } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';

import BulkOperationProgressCard from '~components/BulkOperationProgress/BulkOperationProgressCard';
import BulkOperationProgressStack from '~components/BulkOperationProgress/BulkOperationProgressStack';
import {
  initialBulkOperationProgress,
  isBulkOperationSettled,
  mergeBulkOperationProgress,
  subscribeToBulkOperation,
} from '~utils/rtu/bulkOperation';

// Long enough to read the result, short enough that a string of sends doesn't pile up cards.
const SUCCEEDED_CARD_DISMISS_DELAY_IN_MS = 5000;

interface UseBulkOperationProgressOptions<T extends BulkOperation_Read> {
  channel: string;
  onSettled?: (progress: T) => void;
  statusText: (progress: T) => string;
  title: string;
}

export default function useBulkOperationProgress<T extends BulkOperation_Read>({
  channel,
  onSettled,
  statusText,
  title,
}: UseBulkOperationProgressOptions<T>) {
  const [operationIds, setOperationIds] = useState<string[]>([]);
  const [progressByOperationId, setProgressByOperationId] = useState<Record<string, T>>(
    {},
  );

  const subscriptionsByOperationId = useRef<Record<string, Subscription | undefined>>({});
  const dismissTimeoutsByOperationId = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const settledOperationIds = useRef(new Set<string>());
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const dismiss = useCallback((operationId: string) => {
    subscriptionsByOperationId.current[operationId]?.unsubscribe();
    delete subscriptionsByOperationId.current[operationId];
    clearTimeout(dismissTimeoutsByOperationId.current[operationId]);
    delete dismissTimeoutsByOperationId.current[operationId];
    settledOperationIds.current.delete(operationId);

    setOperationIds((ids) => ids.filter((id) => id !== operationId));
    setProgressByOperationId((state) => omit(state, operationId));
  }, []);

  // Settles from the merged progress the card shows, not from the raw channel message, so
  // a late message that undercounts failures can't dismiss a card still listing them.
  useEffect(() => {
    Object.entries(progressByOperationId).forEach(([operationId, progress]) => {
      if (
        !isBulkOperationSettled(progress.status) ||
        settledOperationIds.current.has(operationId)
      ) {
        return;
      }

      settledOperationIds.current.add(operationId);
      subscriptionsByOperationId.current[operationId]?.unsubscribe();
      delete subscriptionsByOperationId.current[operationId];
      onSettledRef.current?.(progress);

      const hasSucceededCompletely =
        progress.status === BulkOperationStatus.FINISHED && progress.failed_count === 0;

      if (hasSucceededCompletely) {
        dismissTimeoutsByOperationId.current[operationId] = setTimeout(
          () => dismiss(operationId),
          SUCCEEDED_CARD_DISMISS_DELAY_IN_MS,
        );
      }
    });
  }, [progressByOperationId, dismiss]);

  const track = useCallback(
    (operationId: string, count: number) => {
      setOperationIds((ids) => uniq([...ids, operationId]));
      setProgressByOperationId((state) => ({
        ...state,
        [operationId]:
          state[operationId] ??
          // The initial progress has no channel-specific fields.
          (initialBulkOperationProgress(operationId, count) as T),
      }));

      if (subscriptionsByOperationId.current[operationId]) {
        return;
      }

      subscriptionsByOperationId.current[operationId] = subscribeToBulkOperation<T>(
        channel,
        operationId,
        {
          received: (message) => {
            setProgressByOperationId((state) => ({
              ...state,
              [operationId]: mergeBulkOperationProgress(state[operationId], message),
            }));
          },
        },
      );
    },
    [channel],
  );

  useEffect(() => {
    const subscriptions = subscriptionsByOperationId.current;
    const dismissTimeouts = dismissTimeoutsByOperationId.current;

    return () => {
      Object.values(dismissTimeouts).forEach(clearTimeout);
      Object.values(subscriptions).forEach((subscription) => subscription?.unsubscribe());
    };
  }, []);

  const progressStack = (
    <BulkOperationProgressStack>
      {operationIds.map((operationId) => {
        const progress = progressByOperationId[operationId];

        if (!progress) {
          return null;
        }

        const processedCount = progress.succeeded_count + progress.failed_count;
        const isSettled = isBulkOperationSettled(progress.status);
        const percentComplete =
          isSettled || progress.count === 0
            ? 100
            : (processedCount / progress.count) * 100;

        return (
          <BulkOperationProgressCard
            failedLabels={progress.failed.map((item) => item.label)}
            hasFailures={progress.failed_count > 0}
            isSettled={isSettled}
            key={operationId}
            onClose={() => dismiss(operationId)}
            percentComplete={percentComplete}
            statusText={statusText(progress)}
            title={title}
          />
        );
      })}
    </BulkOperationProgressStack>
  );

  return { progressStack, track };
}
